'use memo';

import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/DataTable';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { DateDurationCell } from '@/features/contract-and-cost/cells/DateDurationCell';
import { useFormatDate, useFormatDistance } from '@/hooks/date';
import { actionsColumn } from '../../-components/-table/actionsColumn';
import { contractNoColumn } from '../../-components/-table/contractNoColumn';
import { contractStatusColumn } from '../../-components/-table/contractStatusColumn';
import { useContractTypeLabels } from './-contractTypeLabels';
import type { ContractAlertItem } from './-query';
const toValidDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
export function useAlertColumns(): ColumnDef<ContractAlertItem>[] {
  const {
    labelFor
  } = useContractTypeLabels();
  return [contractNoColumn<ContractAlertItem>(), {
    id: 'contractTitle',
    accessorKey: 'contractTitle',
    meta: {
      i18nKey: 'contractTitle',
      thStyle: {
        minWidth: '220px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: true,
    sortingFn: 'alphanumeric',
    cell: ({
      row
    }) => <Link to="/contract/$id" params={{
      id: row.original.contractId
    }} className="hover:underline">
          <Text variant="tableItem" className="font-semibold text-inherit">
            {row.original.contractTitle ?? '-'}
          </Text>
        </Link>
  }, contractStatusColumn<ContractAlertItem>(), {
    id: 'contractType',
    accessorKey: 'contractType',
    meta: {
      i18nKey: 'contractType',
      thStyle: {
        minWidth: '150px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: false,
    cell: ({
      row
    }) => <Text variant="tableItem">{labelFor(row.original.contractType)}</Text>
  }, {
    id: 'contractStartDate',
    accessorKey: 'contractStartDate',
    meta: {
      i18nKey: 'contractStartDate',
      thStyle: {
        minWidth: '170px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: true,
    cell: ({
      getValue
    }) => {
      const date = toValidDate(getValue<string | null>());
      return date ? <DateDurationCell date={date} /> : <Text variant="tableItem">–</Text>;
    }
  }, {
    id: 'contractEndDate',
    accessorKey: 'contractEndDate',
    meta: {
      i18nKey: 'contractEndDate',
      thStyle: {
        minWidth: '170px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: true,
    cell: function EndDateCell({
      getValue
    }) {
      const formatDate = useFormatDate();
      const formatDistance = useFormatDistance();
      const date = toValidDate(getValue<string | null>());
      if (!date) return <Text variant="tableItem">–</Text>;
      const now = new Date();
      return <VStack className="gap-0.5">
            <Text variant="label" className="text-neutral-on-fill">
              {formatDate(date, {
            preset: 'date'
          })}
            </Text>
            <Text variant="labelSmallReg" className="text-warning-on-neutral-fill">
              {formatDistance(date, now)}
            </Text>
          </VStack>;
    }
  }, actionsColumn<ContractAlertItem>()];
}